import React, { useState, useEffect, useRef } from 'react';
import { Palette, Check, X, RotateCcw } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const THEME_COLORS = [
  { name: 'Orange', value: '#ea580c' },
  { name: 'Default Blue', value: '#2563eb' },
  { name: 'Violet', value: '#8b5cf6' },
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Emerald', value: '#10b981' },
  { name: 'Rose', value: '#f43f5e' },
  { name: 'Amber', value: '#f59e0b' },
  { name: 'Slate', value: '#475569' },
];

const ThemeCustomizer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { primaryColor, setPrimaryColor } = useTheme();
  const panelRef = useRef(null);

  // Handle click outside to close the panel
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    // Add event listener when panel is open
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle Escape key to close the panel
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleColorSelect = (color) => {
    setPrimaryColor(color);
  };

  const resetTheme = () => {
    setPrimaryColor('#ea580c');
  };

  return (
    <div ref={panelRef} className="fixed right-0 top-1/2 -translate-y-1/2 z-[2000] flex items-start">
      {/* Settings Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`p-3 bg-white dark:bg-slate-800 shadow-enterprise border border-slate-200 dark:border-slate-700 rounded-l-xl transition-all duration-300 hover:bg-slate-50 dark:hover:bg-slate-700 group ${
          isOpen ? 'translate-x-0' : 'translate-x-0'
        }`}
        title="Customize Theme"
      >
        <Palette 
          className={`w-6 h-6 transition-transform duration-500 ${isOpen ? 'rotate-90 text-primary' : 'text-slate-500 dark:text-slate-400 group-hover:rotate-12'}`} 
        />
      </button>

      {/* Customizer Panel */}
      <div
        className={`bg-white dark:bg-slate-800 shadow-focused border border-slate-200 dark:border-slate-700 rounded-l-2xl w-72 transition-all duration-500 ease-in-out absolute top-0 right-[52px] ${
          isOpen 
            ? 'opacity-100 translate-x-0 pointer-events-auto' 
            : 'opacity-0 translate-x-10 pointer-events-none'
        }`}
      >
        <div className="p-5">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Palette className="w-4 h-4 text-primary" />
              Theme Customizer
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={resetTheme}
                className="p-1.5 text-slate-500 hover:text-primary hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                title="Reset to default"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 text-slate-500 hover:text-red-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                Primary Color
              </p>
              <div className="grid grid-cols-4 gap-3">
                {THEME_COLORS.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => handleColorSelect(color.value)}
                    className="relative group p-0.5"
                    title={color.name}
                  >
                    <div
                      className="w-full aspect-square rounded-lg shadow-sm border border-black/5 transition-transform duration-200 group-hover:scale-110"
                      style={{ backgroundColor: color.value }}
                    />
                    {primaryColor === color.value && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <Check className="w-4 h-4 text-white drop-shadow-md" />
                      </div>
                    )}
                    <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100 dark:border-slate-700">
              <p className="text-xs text-slate-400 dark:text-slate-500 leading-relaxed">
                Choose a color to personalize your workspace. Changes are saved automatically.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemeCustomizer;
